// Mensagens assertivas pra erros classificados pelo edge function chat.
// O kind vem de metadata.error.kind na ultima mensagem assistant; se nao
// houver metadata (ex: falha de rede antes do server persistir), usamos
// classifyClientError pra inferir a partir da string do erro.

export type ProviderErrorKind =
  | "context_overflow"
  | "rate_limit"
  | "quota"
  | "upstream_unavailable"
  | "content_filter"
  | "invalid_request"
  | "model_unavailable"
  | "network"
  | "unknown";

export interface ProviderErrorDetails {
  kind: ProviderErrorKind;
  statusCode?: number | null;
  providerCode?: string | null;
  providerMessage?: string;
  providerName?: string | null;
  retryable?: boolean;
}

export function friendlyProviderError(
  d: ProviderErrorDetails,
  modelLabel?: string,
): { title: string; description?: string } {
  const model = modelLabel ? ` (${modelLabel})` : "";
  switch (d.kind) {
    case "context_overflow":
      return {
        title: `A conversa excedeu a janela de contexto do modelo${model}.`,
        description:
          "Bifurque a partir de uma mensagem recente ou inicie uma nova conversa. Você pode pedir um resumo antes de continuar em outra conversa.",
      };
    case "rate_limit":
      return {
        title: "Limite de requisições atingido no provedor.",
        description: "Aguarde alguns segundos e tente novamente.",
      };
    case "quota":
      return {
        title: "Cota ou créditos esgotados no provedor.",
        description: "Avise um admin ou selecione outro modelo.",
      };
    case "upstream_unavailable":
      return {
        title: `O provedor do modelo${model} está indisponível${d.statusCode ? ` (HTTP ${d.statusCode})` : ""}.`,
        description: "Tente novamente em instantes ou troque de modelo.",
      };
    case "content_filter":
      return {
        title: "Conteúdo bloqueado pelo filtro de segurança do modelo.",
        description: d.providerMessage,
      };
    case "model_unavailable":
      return {
        title: `O modelo${model} não está disponível.`,
        description: "Selecione outro modelo na barra inferior.",
      };
    case "invalid_request":
      return {
        title: "A requisição foi rejeitada pelo modelo.",
        description: d.providerMessage,
      };
    case "network":
      return {
        title: "Falha de rede ao chamar o modelo.",
        description: "Verifique sua conexão e tente novamente.",
      };
    default:
      return {
        title: "Erro ao gerar resposta.",
        description: d.providerMessage || "Sem detalhes do provedor.",
      };
  }
}

// Heuristica client-side pra quando nao temos metadata.error (ex: falha
// antes do server persistir). Espelha o classificador do edge function
// mas opera só sobre a string do Error.
export function classifyClientError(message: string): ProviderErrorKind {
  const m = (message || "").toLowerCase();
  if (/context length|maximum context|context window|too many tokens|prompt is too long/i.test(m)) return "context_overflow";
  if (/rate.?limit|429|too many requests/i.test(m)) return "rate_limit";
  if (/quota|insufficient.*(credit|balance|fund)|payment required|402/i.test(m)) return "quota";
  if (/content.?filter|safety|moderation/i.test(m)) return "content_filter";
  if (/model.*(not found|unavailable|does not exist)/i.test(m)) return "model_unavailable";
  if (/worker_resource_limit|memory limit|not having enough compute/i.test(m)) return "upstream_unavailable";
  if (/idle_timeout|gateway timeout|504|timeout/i.test(m)) return "upstream_unavailable";
  if (/fetch failed|network|failed to fetch/i.test(m)) return "network";
  if (/5\d\d|server error/i.test(m)) return "upstream_unavailable";
  if (/400|422|invalid/i.test(m)) return "invalid_request";
  return "unknown";
}
